import { useEffect, useState } from "react";
import axios from "axios";
import confetti from "canvas-confetti";
import { Apple, Play } from "lucide-react";
import { API } from "../lib/api";
import { APP_STORE_URL, PLAY_STORE_URL, LAUNCH_DATE } from "../lib/links";

const remaining = (launchDate) => Math.max(0, new Date(launchDate) - Date.now());

const CONFETTI_KEY = "freshly_launch_confetti";
const CONFETTI_COLORS = ["#10B981", "#042F1E", "#F7F5EF", "#F59E0B"];

const fireLaunchConfetti = () => {
  const fire = (ratio, opts) =>
    confetti({ particleCount: Math.floor(240 * ratio), colors: CONFETTI_COLORS, disableForReducedMotion: true, ...opts });
  fire(0.4, { spread: 70, origin: { y: 0.6 } });
  setTimeout(() => fire(0.3, { angle: 60, spread: 80, origin: { x: 0, y: 0.7 } }), 250);
  setTimeout(() => fire(0.3, { angle: 120, spread: 80, origin: { x: 1, y: 0.7 } }), 450);
  setTimeout(() => fire(0.35, { spread: 120, startVelocity: 45, origin: { y: 0.55 } }), 800);
};

const Badge = ({ href, testid, icon, top, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    data-testid={testid}
    className="inline-flex items-center gap-3 rounded-2xl bg-forest px-5 py-3 text-left text-bone transition-transform duration-300 hover:scale-[1.03]"
  >
    {icon}
    <span>
      <span className="block text-[10px] uppercase tracking-wider opacity-70">{top}</span>
      <span className="block font-manrope text-sm font-bold">{label}</span>
    </span>
  </a>
);

const Countdown = ({ ms, launchDate }) => {
  const s = Math.floor(ms / 1000);
  const units = [
    [Math.floor(s / 86400), "Days"],
    [Math.floor(s / 3600) % 24, "Hours"],
    [Math.floor(s / 60) % 60, "Min"],
    [s % 60, "Sec"],
  ];
  const launchLabel = new Date(launchDate).toLocaleDateString("en-CA", { month: "short", day: "numeric" });
  return (
    <div data-testid="launch-countdown" className="inline-flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {units.map(([v, l]) => (
          <div
            key={l}
            data-testid={`countdown-${l.toLowerCase()}`}
            className="flex min-w-[76px] flex-col items-center rounded-2xl bg-forest px-4 py-3 text-bone"
          >
            <span className="font-syne text-2xl font-extrabold tabular-nums">{String(v).padStart(2, "0")}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">{l}</span>
          </div>
        ))}
      </div>
      <p className="text-xs font-semibold text-forest/50">
        The Freshly app drops {launchLabel} — store badges go live right here the second it lands.
      </p>
    </div>
  );
};

export const StoreCta = () => {
  const [cfg, setCfg] = useState(null);
  const launchDate = cfg?.launch_date || LAUNCH_DATE;
  const [ms, setMs] = useState(() => remaining(LAUNCH_DATE));

  useEffect(() => {
    axios.get(`${API}/public-config`).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setMs(remaining(launchDate));
    const t = setInterval(() => setMs(remaining(launchDate)), 1000);
    return () => clearInterval(t);
  }, [launchDate]);

  useEffect(() => {
    if (ms !== 0) return;
    const recentLaunch = Date.now() - new Date(launchDate) < 48 * 3600 * 1000;
    if (recentLaunch && !localStorage.getItem(CONFETTI_KEY)) {
      localStorage.setItem(CONFETTI_KEY, "1");
      fireLaunchConfetti();
    }
  }, [ms, launchDate]);

  if (ms > 0) return <Countdown ms={ms} launchDate={launchDate} />;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Badge
        href={cfg?.app_store_url || APP_STORE_URL}
        testid="app-store-badge"
        icon={<Apple className="h-7 w-7" />}
        top="Download on the"
        label="App Store"
      />
      <Badge
        href={cfg?.play_store_url || PLAY_STORE_URL}
        testid="google-play-badge"
        icon={<Play className="h-6 w-6 fill-current" />}
        top="Get it on"
        label="Google Play"
      />
    </div>
  );
};
